import { ModalWrapper } from '@/shared';
import type React from 'react';
import { useEffect, useState } from 'react';

interface DeleteResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (id: string) => void;
  resource: { id: string };
  isLoading?: boolean;
}

export const DeleteResourceModal: React.FC<DeleteResourceModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  resource,
  isLoading = false,
}) => {
  const [isChecked, setIsChecked] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleConfirmDelete = () => {
    if (!isChecked) {
      setShowError(true);
      return;
    }
    onConfirm(resource.id);
  };

  const handleCancleDelete = () => {
    if (isChecked) {
      setIsChecked(false);
      setShowError(false);
    }
    onClose();
  };

  // Reset local state whenever modal opens or the selected house changes.
  // This prevents the "checkbox stays checked" bug when reusing the same modal instance.
  useEffect(() => {
    if (isOpen) {
      setIsChecked(false);
      setShowError(false);
    }
  }, [isOpen, resource.id]);
  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-center w-full min-h-full">
        <div className="w-xl p-4 md:p-8 bg-white rounded-4xl">
          <svg
            className="mb-8"
            width="52"
            height="64"
            viewBox="0 0 52 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M15.3501 1.77785C15.3502 1.30704 15.5372 0.855532 15.8701 0.522537C16.2029 0.189543 16.6543 0.00230787 17.1251 0.00197656L34.874 0.000976562C35.3449 0.00206831 35.7962 0.189822 36.1289 0.523084C36.4616 0.856346 36.6487 1.30793 36.649 1.77885V6.16848H15.3501V1.77785ZM44.5327 61.4582C44.4875 62.1504 44.1794 62.7991 43.6715 63.2715C43.1636 63.7439 42.4944 64.0043 41.8007 63.9995H9.99436C9.30093 63.9976 8.63389 63.7335 8.12717 63.2601C7.62046 62.7867 7.3116 62.1392 7.26261 61.4475L4.54086 21.6327H47.4376L44.5334 61.4577L44.5327 61.4582ZM51.6037 18.0267H0.396484V13.9017C0.397246 12.8077 0.832125 11.7587 1.60564 10.985C2.37915 10.2113 3.42808 9.77622 4.52211 9.77523L47.4776 9.77385C48.5715 9.77554 49.6202 10.211 50.3934 10.9848C51.1667 11.7585 51.6016 12.8074 51.6026 13.9014V18.0264L51.6037 18.0267ZM17.755 54.5081C17.755 54.7448 17.8016 54.9793 17.8922 55.198C17.9828 55.4167 18.1156 55.6154 18.283 55.7828C18.4504 55.9502 18.6491 56.083 18.8679 56.1736C19.0866 56.2642 19.321 56.3108 19.5577 56.3108C19.7945 56.3108 20.0289 56.2642 20.2476 56.1736C20.4663 56.083 20.6651 55.9502 20.8325 55.7828C20.9999 55.6154 21.1327 55.4167 21.2233 55.198C21.3139 54.9793 21.3605 54.7448 21.3605 54.5081V29.2272C21.3567 28.7518 21.1652 28.2971 20.8277 27.9622C20.4903 27.6273 20.0341 27.4393 19.5587 27.4391C19.0832 27.4389 18.6269 27.6266 18.2892 27.9613C17.9515 28.296 17.7597 28.7505 17.7556 29.226V54.5081H17.755ZM30.6175 54.5081C30.6175 54.9863 30.8074 55.4449 31.1456 55.783C31.4837 56.1211 31.9423 56.3111 32.4205 56.3111C32.8987 56.3111 33.3573 56.1211 33.6954 55.783C34.0335 55.4449 34.2235 54.9863 34.2235 54.5081V29.2272C34.2197 28.7517 34.0282 28.297 33.6907 27.962C33.3531 27.6271 32.8969 27.439 32.4214 27.4389C31.9459 27.4387 31.4896 27.6264 31.1518 27.9611C30.8141 28.2959 30.6222 28.7505 30.6181 29.226L30.6175 54.5081Z"
              fill="#E03A3D"
            />
          </svg>

          <div className="mb-4 md:mb-8">
            <h4 className="text-lg md:text-3xl font-bold text-text-primary mb-2 ">
              Delete resource
            </h4>
            <p className="text-base md:text-xl text-text-secondary ">
              This action cannot be undone. The selected resource and all its
              related data will be permanently deleted.
            </p>
          </div>

          <div className="flex items-center gap-4 mb-4 md:mb-8">
            <input
              type="checkbox"
              id="confirmDelete"
              checked={isChecked}
              onChange={(e) => {
                setIsChecked(e.target.checked);
                if (e.target.checked) setShowError(false);
              }}
              className="mt-1 cursor-pointer"
            />
            <label
              htmlFor="confirmDelete"
              className="text-text-secondary cursor-pointer"
            >
              I understand that this resource and its details will be deleted
              permanently.
            </label>
          </div>

          {/* Error message */}
          {showError && (
            <p className="text-sm text-red-500 mb-4">
              Please confirm before deleting this announcement.
            </p>
          )}

          <div className="grid grid-cols-2 gap-4">
            <button
              className="p-2  text-[#5761EB] text-base md:text-lg font-bold  cursor-pointer rounded-md hover:bg-[#5761EB] hover:text-white transition-colors"
              onClick={handleCancleDelete}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className={`p-2 text-base md:text-lg font-bold rounded-md border transition-colors ${
                isChecked
                  ? 'border-[#891518] text-[#891518] hover:bg-[#891518] hover:text-white'
                  : 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-100'
              } ${isLoading ? 'opacity-50 cursor-wait' : ''}`}
              onClick={handleConfirmDelete}
              disabled={!isChecked || isLoading}
            >
              {isLoading ? 'Deleting...' : 'Delete Resource'}
            </button>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};
